import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { formatarDataHora } from "../../utils/dateUtils";
import axios from "axios";

export default function LinkExternoValidacao() {
  const { codigo } = useParams();

  const [status, setStatus] = useState("validando"); // validando, valido, invalido, enviado, erro
  const [mensagemErro, setMensagemErro] = useState("");
  const [dadosLink, setDadosLink] = useState(null);

  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    async function validarLink() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/externo/certificado/${codigo}`);
        setDadosLink(res.data.data);
        setStatus("valido");
      } catch (err) {
        console.error(err);
        setMensagemErro(err?.response?.data?.message ?? "Link inválido ou expirado");
        setStatus("invalido");
      }
    }
    validarLink();
  }, [codigo]);

  const handleEnviar = async () => {
    if (!arquivo) {
      setMensagemErro("Selecione o arquivo do certificado");
      return;
    }
    if (!senha) {
      setMensagemErro("Informe a senha do certificado");
      return;
    }

    setEnviando(true);
    setMensagemErro("");

    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);
      formData.append("senha", senha);

      await axios.post(
        `${process.env.REACT_APP_API}/externo/certificado/${codigo}`,
        formData
      );

      setStatus("enviado");
    } catch (err) {
      console.error(err);
      setMensagemErro(err?.response?.data?.message ?? "Erro ao enviar certificado");
      setStatus("erro");
    } finally {
      setEnviando(false);
    }
  };

  // Tela de carregamento
  if (status === "validando") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Validando link...</p>
        </div>
      </div>
    );
  }

  // Tela de link inválido
  if (status === "invalido") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Link Inválido</h1>
          <p className="text-gray-600">{mensagemErro}</p>
        </div>
      </div>
    );
  }

  // Tela de sucesso
  if (status === "enviado") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Certificado Enviado!</h1>
          <p className="text-gray-600">
            Seu certificado foi enviado com sucesso para <strong>{dadosLink?.empresaNome}</strong>.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Você pode fechar esta página.
          </p>
        </div>
      </div>
    );
  }

  // Formulário de envio
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Enviar Certificado Digital</h1>
          <p className="text-gray-600 mt-1">
            para <strong>{dadosLink?.empresaNome}</strong>
          </p>

          {dadosLink?.dataExpiracao && (
            <p className="text-sm text-amber-600 mt-2">
              ⚠️ Este link expira em {formatarDataHora(dadosLink.dataExpiracao)}
            </p>
          )}
        </div>

        {/* Mensagem de erro */}
        {mensagemErro && (status === "valido" || status === "erro") && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {mensagemErro}
          </div>
        )}

        {/* Formulário */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Arquivo do Certificado *
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".pfx,.p12"
                onChange={(e) => {
                  setArquivo(e.target.files?.[0] || null);
                  setMensagemErro("");
                }}
                className="w-full p-3 border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Formatos aceitos: .pfx, .p12
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha do Certificado *
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => {
                setSenha(e.target.value);
                setMensagemErro("");
              }}
              className="w-full p-3 border rounded-lg"
              placeholder="Digite a senha do certificado"
            />
          </div>

          <button
            onClick={handleEnviar}
            disabled={enviando}
            className="w-full py-3 px-4 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {enviando ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Enviando...
              </span>
            ) : (
              "Enviar Certificado"
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t text-center">
          <p className="text-xs text-gray-500">
            Seu certificado será armazenado de forma segura e criptografada.
          </p>
        </div>
      </div>
    </div>
  );
}